import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loadClientUsers, peekClientUsers } from './clientUsersCache';
import {
  ClientUserAccess,
  clientUserDisplayName,
} from '../../types/clientAccess';
import { useI18n } from '../../contexts/I18nContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useAuth } from '../../contexts/Auth/authContext';
import { isAbortError } from '../../utils/abortError';
import { UserDetailModal } from '../users/UserDetailModal';
import { UserAvatar } from '../users/UserAvatar';
import { UserWithDetails } from '../../types/user';

interface ClientUsersTabProps {
  clientId: string;
  clientName: string;
  /** Called after the client's own access flag is toggled here. */
  onClientAccessChanged?: (active: boolean) => void;
  /**
   * Whether this client's users may use the application at all. Distinct
   * from the client's business status: cutting application access says
   * nothing about the fleet. When false, per-user toggles are moot.
   */
  clientAccess: boolean;
}

type Filter = 'all' | 'active' | 'revoked' | 'admins';

/**
 * Visual weight per role. ClientAdmin — the customer's own administrator, who
 * manages the other technicians — reads as the emphasised one; ordinary
 * technicians stay neutral so the hierarchy is visible without reading.
 */
const roleTone = (role: string): string => {
  switch (role) {
    case 'SuperAdmin':
    case 'Admin':
    case 'Operator':
      return 'role-badge is-anep';
    case 'ClientAdmin':
      return 'role-badge is-clientadmin';
    default:
      return 'role-badge is-standard';
  }
};

const isClientAdmin = (user: ClientUserAccess) => user.roles.includes('ClientAdmin');

/**
 * The accounts of one client: a summary strip (accounts, active, suspended,
 * administrators), a search box and a filter, then one row per account
 * with initials, name and login, email, roles, status and creation date.
 * A row opens the account detail view.
 */
export const ClientUsersTab: React.FC<ClientUsersTabProps> = ({
  clientId,
  clientName,
  clientAccess,
}) => {
  const { formatDate, formatNumber, t } = useI18n();
  const { isSuperAdmin, isAdmin } = useClientAccess();
  const { user: currentUser } = useAuth();
  const showsEveryone = isSuperAdmin || isAdmin;

  const [selectedUser, setSelectedUser] = useState<UserWithDetails | null>(null);
  // What was remembered for this client shows at once; the spinner is for
  // a client never opened in this session.
  const remembered = peekClientUsers(clientId);
  const [users, setUsers] = useState<ClientUserAccess[]>(remembered?.users ?? []);
  const [env, setEnv] = useState<string>(remembered?.env ?? '');
  const [loading, setLoading] = useState(remembered === null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const fetchUsers = useCallback(async () => {
    const hadSomething = peekClientUsers(clientId) !== null;
    try {
      if (hadSomething) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const response = await loadClientUsers(clientId);
      setUsers(response.users || []);
      setEnv(response.env || '');
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[CLIENT USERS] load failed', err);
      setError(t('clients.users.errorLoading'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /** Open the account detail view for a row. */
  const openUser = (user: ClientUserAccess) =>
    setSelectedUser({
      id: user.id,
      username: user.username,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      enabled: user.enabled,
      emailVerified: false,
      fullName: clientUserDisplayName(user),
      createdDate: user.createdTimestamp ? new Date(user.createdTimestamp) : undefined,
    });

  // A customer's admin manages their technicians, not themselves: seeing
  // your own row next to a switch that would lock you out is a trap.
  // ANEP staff keep seeing everyone, including themselves.
  const scoped = useMemo(
    () => users.filter((u) => showsEveryone || u.id !== currentUser?.id),
    [users, showsEveryone, currentUser?.id],
  );

  const visibleUsers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return scoped.filter((user) => {
      if (filter === 'active' && !user.enabled) return false;
      if (filter === 'revoked' && user.enabled) return false;
      if (filter === 'admins' && !isClientAdmin(user)) return false;
      if (!needle) return true;
      return (
        user.username.toLowerCase().includes(needle) ||
        (user.email || '').toLowerCase().includes(needle) ||
        clientUserDisplayName(user).toLowerCase().includes(needle)
      );
    });
  }, [scoped, filter, search]);

  const facts = {
    total: scoped.length,
    active: scoped.filter((u) => u.enabled).length,
    suspended: scoped.filter((u) => !u.enabled).length,
    admins: scoped.filter(isClientAdmin).length,
  };

  if (loading) {
    return (
      <div className="client-users-tab users-state" data-testid="client-users-loading">
        <div className="users-spinner" />
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="client-users-tab" data-testid="client-users-tab">
      <div className="users-toolbar">
        <input
          type="search"
          className="users-search"
          data-testid="client-users-search"
          placeholder={t('clients.users.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('clients.users.searchPlaceholder')}
        />
        <select
          className="users-select"
          data-testid="client-users-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
          aria-label={t('clients.users.statusColumn')}
        >
          <option value="all">{t('clients.users.filterAll')}</option>
          <option value="active">{t('clients.users.filterActive')}</option>
          <option value="revoked">{t('clients.users.filterRevoked')}</option>
          <option value="admins">{t('clients.users.filterAdmins')}</option>
        </select>
        <div className="users-toolbar-facts" data-testid="client-users-facts">
          <span><strong>{formatNumber(facts.total)}</strong> {t('users.summary.accounts')}</span>
          <span className="is-ok"><strong>{formatNumber(facts.active)}</strong> {t('users.summary.active')}</span>
          {facts.suspended > 0 && <span className="is-warning"><strong>{formatNumber(facts.suspended)}</strong> {t('users.summary.suspended')}</span>}
          <span><strong>{formatNumber(facts.admins)}</strong> {t('users.summary.admins')}</span>
          {env && <span className="env-badge" title={t('clients.users.envHint')}>{env}</span>}
        </div>
        <button type="button" className="btn btn-secondary btn-sm users-refresh" data-testid="client-users-refresh" onClick={fetchUsers} disabled={refreshing} aria-busy={refreshing}>
          {refreshing ? t('users.refreshing') : t('common.refresh')}
        </button>
      </div>

      {!clientAccess && (
        <div className="client-users-notice warning" data-testid="client-inactive-notice">
          {t('clients.users.clientInactiveNotice', { client: clientName })}
        </div>
      )}

      {error && (
        <div className="client-users-notice error" data-testid="client-users-error">
          {error}
        </div>
      )}

      {visibleUsers.length === 0 ? (
        <div className="users-empty users-card-empty" data-testid="client-users-empty">
          {users.length === 0 ? t('clients.users.emptyNoUsers') : t('clients.users.emptyNoMatch')}
        </div>
      ) : (
        <div className="users-table-wrap">
          <table className="users-table-v2 client-users-table" data-testid="client-users-table">
            <thead>
              <tr>
                <th>{t('users.summary.account')}</th>
                <th>{t('users.email')}</th>
                <th>{t('users.roles')}</th>
                <th>{t('clients.users.statusColumn')}</th>
                <th>{t('clients.created')}</th>
                <th className="users-col-open"><span className="sr-only">{t('users.overview.open')}</span></th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((user) => (
                <tr
                  key={user.id}
                  className={['users-row', user.enabled ? '' : 'is-off row-revoked', isClientAdmin(user) ? 'row-clientadmin' : ''].filter(Boolean).join(' ')}
                  data-testid={`client-user-row-${user.username}`}
                  onClick={() => openUser(user)}
                >
                  <td>
                    <div className="users-identity">
                      <UserAvatar username={user.username} firstName={user.firstName} lastName={user.lastName} enabled={user.enabled} />
                      <div className="users-identity-text">
                        <button
                          type="button"
                          className="users-name-link user-name-link"
                          data-testid={`open-user-${user.username}`}
                          onClick={(e) => { e.stopPropagation(); openUser(user); }}
                        >
                          {clientUserDisplayName(user)}
                        </button>
                        <span className="users-sub mono">
                          {user.username}
                          {user.legacyAccess && (
                            <span className="status-badge legacy users-legacy" title={t('clients.users.legacyHint')}>{t('clients.users.legacyBadge')}</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="users-email">{user.email || <span className="users-muted">–</span>}</td>
                  <td>
                    {user.roles.length > 0 ? (
                      <div className="role-badges">
                        {user.roles.map((role) => <span key={role} className={roleTone(role)}>{role}</span>)}
                      </div>
                    ) : (
                      <span className="users-muted">{t('clients.users.noRole')}</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
                      {user.enabled ? t('clients.users.statusActive') : t('clients.users.statusSuspended')}
                    </span>
                  </td>
                  <td className="users-muted">{user.createdTimestamp ? formatDate(new Date(user.createdTimestamp)) : '–'}</td>
                  <td className="users-col-open"><span className="users-chevron" aria-hidden="true">›</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <UserDetailModal
        user={selectedUser}
        isOpen={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        onUserUpdated={() => {
          setSelectedUser(null);
          fetchUsers();
        }}
      />
    </div>
  );
};
